// Helpers shared by the MusicXML and MIDI converters.

import { type Duration, type DurBase, type Step, type Pitch, mod, normalizeFifths, durationForTicks, ticks, splitTicks, spellMidi, fifthsOf } from '@simplenotation/theory';
import type { KeySignature, Track } from '../model/types';

/** Fifths of the written transposition interval (B♭ instrument → +2, E♭ alto → +3, octave → 0). */
export function transpositionFifths(writtenToSounding: number): number {
  const k = mod(-writtenToSounding * 7, 12);
  return k > 6 ? k - 12 : k;
}

/** Written key of a track for a concert key. */
export function writtenKey(concert: KeySignature, track: Pick<Track, 'writtenToSounding'>): KeySignature {
  const shift = transpositionFifths(track.writtenToSounding);
  if (!shift) return concert;
  return { fifths: normalizeFifths(concert.fifths + shift).fifths, mode: concert.mode };
}

/** Concert key from a track's written key. */
export function concertKey(written: KeySignature, writtenToSounding: number): KeySignature {
  const shift = transpositionFifths(writtenToSounding);
  if (!shift) return written;
  return { fifths: normalizeFifths(written.fifths - shift).fifths, mode: written.mode };
}

/**
 * Spell a MIDI number in a key with spellMidi, choosing sharps or flats for
 * chromatic notes so that the spelling lies in a 12-wide line-of-fifths window
 * around the key (C major: C♯ E♭ F♯ A♭ B♭; A minor: G♯ for the leading tone).
 */
export function spellInKey(midi: number, key: KeySignature): Pitch {
  const lo = key.fifths - (key.mode === 'minor' ? 3 : 4);
  const sharp = spellMidi(midi, key.fifths, false);
  const lof = fifthsOf(sharp);
  return spellMidi(midi, key.fifths, !(lof >= lo && lof <= lo + 11));
}

export const TYPE_NAMES: Record<DurBase, string> = {
  1: 'whole',
  2: 'half',
  4: 'quarter',
  8: 'eighth',
  16: '16th',
  32: '32nd',
  64: '64th',
};

const TYPE_BASES: Record<string, number> = {
  maxima: 0.125,
  long: 0.25,
  breve: 0.5,
  whole: 1,
  half: 2,
  quarter: 4,
  eighth: 8,
  '16th': 16,
  '32nd': 32,
  '64th': 64,
  '128th': 128,
  '256th': 256,
  '512th': 512,
  '1024th': 1024,
};

/** Note type name → base (may be outside the model's range; callers clamp). */
export function baseOfType(name: string | undefined): number | undefined {
  if (!name) return undefined;
  return TYPE_BASES[name.trim()];
}

/**
 * Durations (in order, to be tied) for a tick length. Uses an exact
 * single duration when possible, else a triplet, else a tied split.
 */
export function durationsForTicks(t: number): Duration[] {
  const exact = durationForTicks(t);
  if (exact) return [exact];
  for (const [actual, normal] of [
    [3, 2],
    [5, 4],
    [6, 4],
    [7, 4],
    [7, 8],
    [9, 8],
    [2, 3],
    [4, 3],
  ]) {
    const plain = durationForTicks((t * actual) / normal);
    if (plain && plain.dots === 0) {
      const d: Duration = { ...plain, tuplet: { actual, normal } };
      if (Math.abs(ticks(d) - t) < 1e-6) return [d];
    }
  }
  return splitTicks(t);
}

// ---------------------------------------------------------------- chord symbols

export interface ParsedChord {
  root: { step: Step; alter: number } | null;
  /** Display suffix after the root (e.g. "m7", "maj9"). */
  suffix: string;
  kind: string;
  bass?: { step: Step; alter: number };
}

const ACC_RE = '(bb|b|♭|𝄫|##|#|♯|x|𝄪)?';
const alterOf = (acc: string | undefined): number =>
  !acc ? 0 : acc === 'bb' || acc === '𝄫' ? -2 : acc === 'b' || acc === '♭' ? -1 : acc === '#' || acc === '♯' ? 1 : 2;

/** Suffix → MusicXML kind, longest match first. */
const KIND_TABLE: Array<[string, string]> = [
  ['maj13', 'major-13th'],
  ['maj11', 'major-11th'],
  ['maj9', 'major-ninth'],
  ['maj7', 'major-seventh'],
  ['Maj7', 'major-seventh'],
  ['ma7', 'major-seventh'],
  ['M7', 'major-seventh'],
  ['Δ7', 'major-seventh'],
  ['Δ', 'major-seventh'],
  ['m(maj7)', 'major-minor'],
  ['mMaj7', 'major-minor'],
  ['mmaj7', 'major-minor'],
  ['mM7', 'major-minor'],
  ['m7b5', 'half-diminished'],
  ['m7♭5', 'half-diminished'],
  ['ø7', 'half-diminished'],
  ['ø', 'half-diminished'],
  ['min13', 'minor-13th'],
  ['min11', 'minor-11th'],
  ['min9', 'minor-ninth'],
  ['min7', 'minor-seventh'],
  ['min6', 'minor-sixth'],
  ['min', 'minor'],
  ['m13', 'minor-13th'],
  ['m11', 'minor-11th'],
  ['m9', 'minor-ninth'],
  ['m7', 'minor-seventh'],
  ['m6', 'minor-sixth'],
  ['-7', 'minor-seventh'],
  ['-', 'minor'],
  ['dim7', 'diminished-seventh'],
  ['°7', 'diminished-seventh'],
  ['o7', 'diminished-seventh'],
  ['dim', 'diminished'],
  ['°', 'diminished'],
  ['aug7', 'augmented-seventh'],
  ['+7', 'augmented-seventh'],
  ['7#5', 'augmented-seventh'],
  ['7+5', 'augmented-seventh'],
  ['aug', 'augmented'],
  ['+', 'augmented'],
  ['sus2', 'suspended-second'],
  ['sus4', 'suspended-fourth'],
  ['sus', 'suspended-fourth'],
  ['13', 'dominant-13th'],
  ['11', 'dominant-11th'],
  ['9', 'dominant-ninth'],
  ['7', 'dominant'],
  ['6', 'major-sixth'],
  ['5', 'power'],
  ['m', 'minor'],
  ['maj', 'major'],
  ['M', 'major'],
  ['', 'major'],
];

/** Readable suffix for a MusicXML kind value. */
export const KIND_SUFFIX: Record<string, string> = {
  major: '',
  minor: 'm',
  augmented: 'aug',
  diminished: 'dim',
  dominant: '7',
  'major-seventh': 'maj7',
  'minor-seventh': 'm7',
  'diminished-seventh': 'dim7',
  'augmented-seventh': 'aug7',
  'half-diminished': 'm7b5',
  'major-minor': 'm(maj7)',
  'major-sixth': '6',
  'minor-sixth': 'm6',
  'dominant-ninth': '9',
  'major-ninth': 'maj9',
  'minor-ninth': 'm9',
  'dominant-11th': '11',
  'major-11th': 'maj11',
  'minor-11th': 'm11',
  'dominant-13th': '13',
  'major-13th': 'maj13',
  'minor-13th': 'm13',
  'suspended-second': 'sus2',
  'suspended-fourth': 'sus4',
  power: '5',
  Neapolitan: 'N6',
  Italian: 'It+6',
  French: 'Fr+6',
  German: 'Ger+6',
  pedal: 'ped',
  Tristan: 'Tristan',
  other: '',
  none: 'N.C.',
};

/** Parse a chord name such as "F#m7b5/C" (best effort). */
export function parseChordName(name: string): ParsedChord {
  const s = name.trim();
  const m = new RegExp(`^([A-Ga-g])${ACC_RE}(.*)$`, 'u').exec(s);
  if (!m) return { root: null, suffix: s, kind: /^n\.?c\.?$/i.test(s) ? 'none' : 'other' };
  const root = { step: m[1].toUpperCase() as Step, alter: alterOf(m[2]) };
  let rest = m[3];
  let bass: ParsedChord['bass'];
  const slash = new RegExp(`/([A-G])${ACC_RE}$`, 'u').exec(rest);
  if (slash) {
    bass = { step: slash[1] as Step, alter: alterOf(slash[2]) };
    rest = rest.slice(0, slash.index);
  }
  let kind = 'other';
  for (const [suf, k] of KIND_TABLE) {
    if (rest.startsWith(suf)) {
      kind = k;
      break;
    }
  }
  return { root, suffix: rest, kind, bass };
}

const ALTER_TEXT: Record<number, string> = { [-2]: 'bb', [-1]: 'b', 0: '', 1: '#', 2: '##' };

export function stepAlterText(step: string, alter: number): string {
  return step + (ALTER_TEXT[Math.round(alter)] ?? '');
}
